// A tiny syntax highlighter for the languages used in the lessons.
// Returns an array of lines, each an array of [className, text] tokens.

const CFG_KEYWORDS = /^(?:if|else|switch|case|default|break|while|return|exit|drop|route|request_route|reply_route|onreply_route|failure_route|branch_route|event_route|onsend_route|loadmodule|modparam|include_file|import_file|myself|uri|from_uri|src_ip|status|yes|no|and|or)\b/;

const cfgRules = [
  [/^"(?:[^"\\]|\\.)*"/, 'str'],
  [/^#![^\n]*/, 'pre'],
  [/^(?:#|\/\/)[^\n]*/, 'com'],
  [/^\$\((?:[^()]|\([^()]*\))*\)/, 'pv'],
  [/^\$[A-Za-z_][\w]*(?:\([^)]*\))?/, 'pv'],
  [CFG_KEYWORDS, 'kw'],
  [/^[A-Za-z_][\w]*(?=\s*\()/, 'fn'],
  [/^\d+(?:\.\d+)*(?:\/\d+)?/, 'num'],
  [/^(?:==|!=|=~|!~|&&|\|\||>=|<=|[=!<>+])/, 'op'],
  [/^[A-Za-z_][\w.]*/, 'id'],
  [/^\s+/, ''],
  [/^./, 'punc'],
];

const bashRules = [
  [/^#[^\n]*/, 'com'],
  [/^'[^']*'/, 'str'],
  [/^"(?:[^"\\]|\\.)*"/, 'str'],
  [/^\$\(?[\w]+\)?/, 'pv'],
  [/^--?[A-Za-z][\w-]*/, 'flag'],
  [/^\\$/, 'punc'],
  [/^[^\s'"#$]+/, 'id'],
  [/^\s+/, ''],
  [/^./, 'punc'],
];

const iniRules = [
  [/^[#;][^\n]*/, 'com'],
  [/^\[[^\]]+\]/, 'kw'],
  [/^[\w-]+(?=\s*=)/, 'fn'],
  [/^=/, 'op'],
  [/^[^\n]+/, 'str'],
];

function tokenize(line, rules) {
  const out = [];
  let rest = line;
  let firstWord = true;
  while (rest.length) {
    let matched = false;
    for (const [re, cls] of rules) {
      const m = rest.match(re);
      if (m && m[0].length) {
        let c = cls;
        // in shell snippets the first word of a line is the command
        if (rules === bashRules && cls === 'id' && firstWord) c = 'cmd';
        if (cls !== '') firstWord = false;
        out.push([c, m[0]]);
        rest = rest.slice(m[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      out.push(['', rest]);
      break;
    }
  }
  return out;
}

// strings in cfg get their $variables highlighted too
function splitStringPVs(tokens) {
  const out = [];
  tokens.forEach(([cls, text]) => {
    if (cls !== 'str' || !text.includes('$')) return out.push([cls, text]);
    text.split(/(\$\((?:[^()]|\([^()]*\))*\)|\$[A-Za-z_]\w*(?:\([^)]*\))?)/).forEach((part, i) => {
      if (part) out.push([i % 2 ? 'pv' : 'str', part]);
    });
  });
  return out;
}

function sdpLine(line) {
  const m = line.match(/^([a-z])=(.*)$/);
  if (!m) return [['', line]];
  const attr = m[1] === 'a' ? m[2].match(/^([\w-]+)(:?)(.*)$/) : null;
  if (attr) return [['kw', 'a'], ['op', '='], ['fn', attr[1] + attr[2]], ['str', attr[3]]];
  return [['kw', m[1]], ['op', '='], ['str', m[2]]];
}

function sipLines(lines) {
  let inBody = false;
  return lines.map((line, i) => {
    if (inBody) return sdpLine(line);
    if (line.trim() === '') {
      inBody = true;
      return [['', line]];
    }
    const req = line.match(/^([A-Z]+) (\S+) (SIP\/2\.0)$/);
    if (req) return [['kw', req[1]], ['', ' '], ['pv', req[2]], ['', ' '], ['com', req[3]]];
    const resp = line.match(/^(SIP\/2\.0) (\d{3}) (.*)$/);
    if (resp) return [['com', resp[1]], ['', ' '], ['num', resp[2]], ['', ' '], ['kw', resp[3]]];
    const hdr = line.match(/^([A-Za-z-]+)(:)(.*)$/);
    const value = hdr ? hdr[3] : line;
    const parts = value.split(/(;[\w-]+=?|<[^>]+>|"[^"]*")/).filter(Boolean).map((p) => {
      if (p.startsWith(';')) return ['flag', p];
      if (p.startsWith('<')) return ['pv', p];
      if (p.startsWith('"')) return ['str', p];
      return ['', p];
    });
    return hdr ? [['fn', hdr[1]], ['op', hdr[2]], ...parts] : parts;
  });
}

export default function highlight(lang, text) {
  const lines = text.split('\n');
  switch (lang) {
    case 'cfg':
      return lines.map((l) => splitStringPVs(tokenize(l, cfgRules)));
    case 'bash':
      return lines.map((l) => tokenize(l, bashRules));
    case 'ini':
      return lines.map((l) => tokenize(l, iniRules));
    case 'sip':
      return sipLines(lines);
    case 'sdp':
      return lines.map(sdpLine);
    default:
      return lines.map((l) => [['', l]]);
  }
}
